import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputSwitch } from 'primereact/inputswitch';
import { useTrainingSessions } from '../../hooks/coach/useTrainingSessions';
import { AssignWorkoutDialog } from '../dialogs/AssignWorkoutDialog';
import { PlanDetailsDialog } from '../dialogs/PlanDetailsDialog';
import { PlanCard, IPlan } from './PlanCard';

export function TrainingSessionsTab() {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const [detailsPlanId, setDetailsPlanId] = useState<number | null>(null);
  const [detailsVisible, setDetailsVisible] = useState<boolean>(false);
  const [, setDetailsRefreshKey] = useState<number>(0);
  const [, setDetailsLoading] = useState<boolean>(false);
  const [search, setSearch] = useState('');

  const {
    workouts,
    selectedWorkouts,
    setSelectedWorkouts,
    selectedClient,
    setSelectedClient,
    isDialogVisible,
    setDialogVisible,
    students,
    filterOption,
    setFilterOption,
    filterOptions,
    handleDelete,
    handleAssignToClient,
    handleOpenAssignDialog
  } = useTrainingSessions();

  const filteredWorkouts = useMemo<IPlan[]>(() => {
    const term = search.trim().toLowerCase();
    if (!term) return workouts;
    return workouts.filter((w: IPlan) => w.planName?.toLowerCase().includes(term));
  }, [workouts, search]);

  const stats = useMemo(() => {
    const total = workouts.length;
    const assigned = workouts.filter((w: IPlan) => (w.clientWorkouts?.length ?? 0) > 0).length;
    return { total, assigned, unassigned: total - assigned };
  }, [workouts]);

  const [showExercises, setShowExercises] = useState(false);

  const toggleSelect = (plan: IPlan, checked: boolean) => {
    if (checked) {
      setSelectedWorkouts([...selectedWorkouts, plan]);
    } else {
      setSelectedWorkouts(selectedWorkouts.filter((w: IPlan) => w.id !== plan.id));
    }
  };

  const clearSelection = () => setSelectedWorkouts([]);

  return (
    <div style={{ paddingBottom: selectedWorkouts.length > 0 ? '5rem' : '0.5rem' }}>
      {/* Header */}
      <div className="flex flex-column gap-1 mb-3">
        <h2
          style={{
            margin: 0,
            fontSize: '1.4rem',
            fontWeight: 800,
            letterSpacing: '-0.02em',
            color: 'var(--ios-text)'
          }}
        >
          <FormattedMessage id="coach.sections.trainingPlans" />
        </h2>
        <small style={{ color: 'var(--ios-text-secondary)', fontSize: '0.82rem' }}>
          <FormattedMessage id="coach.plan.description" />
        </small>
      </div>

      {/* Stats */}
      <div className="flex flex-wrap gap-2 mb-3">
        <StatPill label={intl.formatMessage({ id: 'plansPage.stats.total' })} value={stats.total} color="#4d7c0f" />
        <StatPill
          label={intl.formatMessage({ id: 'plansPage.stats.assigned' })}
          value={stats.assigned}
          color="#22c55e"
        />
        <StatPill
          label={intl.formatMessage({ id: 'plansPage.stats.unassigned' })}
          value={stats.unassigned}
          color="#f59e0b"
        />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 align-items-center mb-3">
        <span className="p-input-icon-left" style={{ flex: '1 1 220px', minWidth: 0 }}>
          <i className="pi pi-search" />
          <InputText
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={intl.formatMessage({ id: 'plansPage.search.placeholder' })}
            className="w-full"
          />
        </span>
        <div className="et-filter-chips" role="tablist">
          {filterOptions.map((opt) => {
            const active = opt.value === filterOption;
            return (
              <button
                key={String(opt.value)}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setFilterOption(opt.value)}
                className={`et-filter-chip${active ? ' is-active' : ''}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <Button
          label={intl.formatMessage({ id: 'coach.buttons.newPlan' })}
          icon="pi pi-plus"
          className="p-button-sm"
          onClick={() =>
            navigate('/plans/create', {
              state: { changeToTemplate: false, returnTo: location.pathname + location.search }
            })
          }
        />
      </div>

      {/* #10: UN switch para todas las sesiones — "El toggle es para todas las
          sesiones o para ninguna", y arriba de la página, no dentro de la card. */}
      <div className="flex align-items-center gap-2 mb-3">
        <InputSwitch
          inputId="show-exercises"
          checked={showExercises}
          onChange={(e) => setShowExercises(Boolean(e.value))}
        />
        <label htmlFor="show-exercises" style={{ fontSize: '0.85rem', color: 'var(--ios-text-secondary)' }}>
          {intl.formatMessage({ id: 'plansPage.showExercises' })}
        </label>
      </div>

      {/* Cards grid */}
      {filteredWorkouts.length === 0 ? (
        <EmptyState
          icon="pi pi-folder-open"
          title={intl.formatMessage({
            id: search ? 'plansPage.empty.search.title' : 'plansPage.empty.title'
          })}
          description={intl.formatMessage({
            id: search ? 'plansPage.empty.search.description' : 'plansPage.empty.description'
          })}
        />
      ) : (
        <div className="grid">
          {filteredWorkouts.map((plan: IPlan) => {
            const isSelected = selectedWorkouts.some((w: IPlan) => w.id === plan.id);
            return (
              <div key={plan.id} className="col-12 sm:col-6 lg:col-4">
                <PlanCard
                  plan={plan}
                  isSelected={isSelected}
                  showExercises={showExercises}
                  onToggleSelect={(checked) => toggleSelect(plan, checked)}
                  onOpenDetails={(instanceId) => {
                    setDetailsPlanId(instanceId);
                    setDetailsVisible(true);
                  }}
                  onView={() => {
                    // #39: "Ver" abre el mismo modal de detalle que ya se usa
                    // desde el client-dashboard, en vez de mandar a la pantalla
                    // /plans/view/:id. Es el mismo comportamiento que clickear
                    // el título de la card, y desde el modal "Editar ejercicios"
                    // lleva a /plans/edit/:id.
                    const instanceId = plan.workoutInstanceTemplates?.[0]?.id;
                    if (instanceId) {
                      setDetailsPlanId(instanceId);
                      setDetailsVisible(true);
                      return;
                    }
                    // Sin instancia no hay detalle que mostrar: se mantiene la
                    // navegación vieja para no dejar el plan inaccesible.
                    navigate(`/plans/view/${plan.id}`, {
                      state: {
                        isEdit: true,
                        changeToTemplate: false,
                        returnTo: location.pathname + location.search
                      }
                    });
                  }}
                  onEdit={() =>
                    navigate(`/plans/edit/${plan.id}`, {
                      state: {
                        isEdit: true,
                        changeToTemplate: false,
                        returnTo: location.pathname + location.search
                      }
                    })
                  }
                  onDelete={(instanceId) => handleDelete(instanceId)}
                  onAssign={() => handleOpenAssignDialog(plan.id)}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Sticky selection bar */}
      {selectedWorkouts.length > 0 && (
        <div
          style={{
            position: 'fixed',
            bottom: '1rem',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--ios-glass-bg)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: '1px solid var(--ios-glass-border)',
            borderRadius: 'var(--ios-radius-pill)',
            boxShadow: 'var(--ios-shadow-xl)',
            padding: '0.5rem 0.75rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            zIndex: 100,
            maxWidth: '92vw'
          }}
        >
          <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--ios-text)', padding: '0 0.4rem' }}>
            {intl.formatMessage({ id: 'plansPage.selection.count' }, { count: selectedWorkouts.length })}
          </span>
          <Button
            icon="pi pi-times"
            className="p-button-text p-button-sm p-button-rounded"
            tooltip={intl.formatMessage({ id: 'plansPage.selection.clear' })}
            tooltipOptions={{ position: 'top' }}
            onClick={clearSelection}
          />
          <Button
            label={intl.formatMessage({ id: 'coach.buttons.assignPlans' })}
            icon="pi pi-user-plus"
            className="p-button-sm"
            onClick={() => setDialogVisible(true)}
          />
        </div>
      )}

      <AssignWorkoutDialog
        visible={isDialogVisible}
        onHide={() => setDialogVisible(false)}
        selectedWorkouts={selectedWorkouts}
        selectedClient={selectedClient}
        setSelectedClient={setSelectedClient}
        students={students}
        onAssign={handleAssignToClient}
      />
      <PlanDetailsDialog
        visible={detailsVisible}
        onHide={() => {
          setDetailsVisible(false);
          setDetailsPlanId(null);
        }}
        planId={detailsPlanId}
        clientId=""
        setRefreshKey={setDetailsRefreshKey}
        setLoading={setDetailsLoading}
        isTemplate
      />
    </div>
  );
}

function StatPill({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.45rem 0.85rem',
        background: 'var(--ios-card-bg)',
        border: '1px solid var(--ios-card-border)',
        borderRadius: 'var(--ios-radius-pill)',
        boxShadow: 'var(--ios-shadow-sm)'
      }}
    >
      <span
        style={{
          width: '0.5rem',
          height: '0.5rem',
          borderRadius: '50%',
          background: color
        }}
      />
      <span style={{ fontSize: '0.75rem', color: 'var(--ios-text-secondary)', fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: '0.95rem', fontWeight: 800, color: 'var(--ios-text)' }}>{value}</span>
    </div>
  );
}

function EmptyState({ icon, title, description }: { icon: string; title: string; description: string }) {
  return (
    <div
      style={{
        background: 'var(--ios-card-bg)',
        border: '1px dashed var(--ios-card-border)',
        borderRadius: 'var(--ios-radius-lg)',
        padding: '2.5rem 1.5rem',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0.5rem'
      }}
    >
      <i className={icon} style={{ fontSize: '2rem', color: 'var(--ios-text-tertiary)', marginBottom: '0.4rem' }} />
      <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--ios-text)' }}>{title}</h4>
      <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--ios-text-secondary)' }}>{description}</p>
    </div>
  );
}
